import { z } from 'zod';

// ---------------------------------------------------------------------------
// Hub operational alerts (Status & Notices) — read surface.
// GET /api/hubs/:hubId/alerts returns the hub's currently-active, tenant-actionable
// connection/credential alerts. Never carries the provider error BODY (it can embed
// a key fragment) — only provider + status_code + category + `detail` (credential-safe
// structured type/code/param) + counts + timestamps.
// ---------------------------------------------------------------------------

export const hubAlertsParam = z.object({
  hubId: z.string().uuid(),
});

export const hubAlertSeverity = z.enum(['critical', 'warn', 'info']);

export const hubAlert = z.object({
  alert_id: z.string(),
  // The hub this alert belongs to — stamped at read time (the per-hub HubDO row
  // doesn't store it). Drives the banner/aggregate deep-link to the hub's Status tab.
  hub_id: z.string(),
  connection_id: z.string().nullable(),
  channel_id: z.string().nullable(),
  // Open string (not an enum): producers define categories as they're wired, and the
  // read surface must not reject a category that ships before this contract updates.
  category: z.string(),
  provider: z.string().nullable(),
  status_code: z.number().nullable(),
  // Credential-safe structured reason (type/code/param, e.g. "invalid_request_error ·
  // response_format") — NEVER the provider error body. Null when the producer had no
  // structured detail. gh #3164.
  detail: z.string().nullable(),
  severity: hubAlertSeverity,
  status: z.enum(['active', 'resolved']),
  count: z.number(),
  first_seen_at: z.string(),
  last_seen_at: z.string(),
  last_notified_at: z.string().nullable(),
  resolved_at: z.string().nullable(),
  // Set when this alert is rolled up at the shared org credential (one org_alert shown
  // against every hub linking it) — the fix lives at the org level, not the hub. Null
  // for hub-local alerts.
  org_credential_id: z.string().nullable(),
});
export type HubAlert = z.infer<typeof hubAlert>;

export const hubAlertsListResponse = z.object({
  alerts: z.array(hubAlert),
});
export type HubAlertsListResponse = z.infer<typeof hubAlertsListResponse>;


// The two expiry alert categories (wayai-pro/wayai#5417), kept zod-free in `constants.ts` so a
// backend module naming them pulls in no contract schema.
export { CREDENTIAL_EXPIRY_ALERT_CATEGORIES } from '../constants';
